import { updateBreadcrumbs } from '@gokwik/utilities'
import JsonDrivenFormWizard from '@library/form-wizard/form-wizard'
import { FormSchema, FormWizardRef, Section, SelectField } from '@library/form-wizard/types'
import { Button, Form, message, Modal } from 'gokwik-ui-kit'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Icon, { LoadingOutlined } from '@ant-design/icons'
import BoltIcon from '@library/images/custom/BoltIcon'
import CashbackIcon from '@library/images/custom/CashbackIcon'
import NoCostEMIIcon from '@library/images/custom/NoCostEMIIcon'
import dayjs from 'dayjs'
import { getCurrencySymbol } from '@library/utilities/helpers/helper'
import Summary, { SummaryRef } from './Summary'
import {
    addRemoveMerchants,
    createDraftOffer,
    getDraftOffer,
    getOfferDropDownData,
    MODE,
    publishDraftOffer,
    resetDropDownData,
    reverseTransformOffer,
    transformValuesToPayload,
    updateDraftOffer,
} from './offer.createEdit.service'
import { getMerchantDetails, getUserDetails, isMerchantSelector } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import MerchantSelector from './MerchantSelector'
import { OFFER_CATEGORY, OfferPageParent } from '../constants'
import { getOfferDetails, updateOffer } from '../offer.service'
import { IDBWrapper } from '@library/utilities/helpers/indexeddb'
import BankCodesInput from './BankCodesInput'

export default function CreateEditOffer({
    breadcrumbs,
    navigate,
    searchParamsFn,
    params,
    parent,
}: {
    breadcrumbs?: []
    navigate?: ReturnType<typeof useNavigate>
    searchParamsFn?: ReturnType<typeof useSearchParams>
    params?
    parent?: OfferPageParent
}) {
    const { id, mode } = params ?? useParams()
    const [searchParams, setSearchParams] = searchParamsFn ?? useSearchParams()
    const [step, setStep] = useState<number>(0)
    const [openLocalDraft, setOpenLocalDraft] = useState(false)
    const localDraft = useRef(null)
    const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null)
    const changedFields = useRef<Set<string>>(new Set())
    const iDb = useRef<IDBWrapper>(new IDBWrapper('gk-dashboard', 'draft-offers'))
    const [prevOfferCategory, setPrevOfferCategory] = useState<OFFER_CATEGORY>(null)
    const [savingDraft, setSavingDraft] = useState<boolean>(false)
    const [saving, setSaving] = useState<boolean>(false)
    const [form] = Form.useForm()
    const summaryRef = useRef<SummaryRef>(null)
    const formRef = useRef<FormWizardRef>(null)
    const [methodOptions, setMethodOptions] = useState<SelectField['options']>([])
    const [cards, setCards] = useState<SelectField['options']>([])
    const [emis, setEmis] = useState<SelectField['options']>([])
    const [banks, setBanks] = useState<SelectField['options']>([])
    const [wallets, setWallets] = useState<SelectField['options']>([])
    const [pspApps, setPspApps] = useState<SelectField['options']>([])
    const [createdBy, setCreatedBy] = useState<string>('')
    const isMerchantUser = useSelector(isMerchantSelector)
    const merchantData = useSelector(getMerchantDetails)
    const userData = useSelector(getUserDetails)
    const currencySymbol = getCurrencySymbol()
    const navigateHook = navigate ?? useNavigate()

    useEffect(() => {
        setStep(+(searchParams.get('step') || 0))

        return () => {
            iDb.current.close()
            resetDropDownData()
        }
    }, [])

    useEffect(() => {
        setSearchParams((prev) => {
            prev.set('step', step.toString())
            return prev
        })
    }, [step])

    const deleteDraft = async () => {
        try {
            await iDb.current.delete(userData?.email || 'local')
        } catch (error) {
            console.log('error on clearing local draft:', error)
        }
    }

    const getLocalStoreId = useCallback(() => {
        switch (mode) {
            case MODE.CREATE:
                return userData?.email || 'local'

            case MODE.DRAFT_EDIT:
            case MODE.EDIT:
                return `${userData?.email}_${mode}_${id}`

            default:
                return 'local'
        }
    }, [mode, userData, id])

    const returnToListing = (data, search?: string) =>
        data
            ? navigateHook(
                  (parent === OfferPageParent.PAYMENT
                      ? '/payment/settings/offers'
                      : '/global-settings/payment-offers') + (search ?? ''),
              )
            : null

    const handleFinish = async () => {
        if (mode === MODE.DRAFT_EDIT) {
            setSaving(true)
            publishDraftOffer(id, (success) => {
                setSaving(false)
                returnToListing(success)
            })
        }
    }

    const moveToFirstError = (field) => {
        document.querySelector(`#${field}`)?.scrollIntoView({ behavior: 'smooth' })
    }

    const handleSaveAsDraft = async () => {
        try {
            setSavingDraft(true)
            await form.validateFields()
            const payload = transformValuesToPayload(
                form.getFieldsValue(true),
                parent === OfferPageParent.PAYMENT ? merchantData?.m_id : undefined,
                mode,
            )
            if (mode === MODE.CREATE) {
                await createDraftOffer(payload, async (data) => {
                    if (data) {
                        await deleteDraft()
                    }
                    returnToListing(data, '?status=draft')
                })
            } else {
                await updateDraftOffer(payload, id, (data) => returnToListing(data, '?status=draft'))
            }
            setSavingDraft(false)
        } catch (error) {
            setSavingDraft(false)
            moveToFirstError(error?.errorFields?.[0]?.name?.[0])
            console.log('Validation failed at save as draft:', error)
        }
    }

    const handleSaveAndNext = async () => {
        try {
            let allowNext = true
            setSaving(true)
            await form.validateFields()
            let payload = transformValuesToPayload(
                form.getFieldsValue(mode === MODE.EDIT ? Array.from(['merchants', ...changedFields.current]) : true),
                parent === OfferPageParent.PAYMENT ? merchantData?.m_id : undefined,
                mode,
            )
            if (parent === OfferPageParent.PAYMENT) {
                delete payload.merchant_ids
            }
            switch (mode) {
                case MODE.CREATE:
                    await createDraftOffer(payload, async (data) => {
                        allowNext = !!data
                        if (data) {
                            await deleteDraft()
                            navigateHook(
                                (parent === OfferPageParent.PAYMENT
                                    ? `/payment/settings/offers/${MODE.DRAFT_EDIT}/`
                                    : `/global-settings/payment-offers/${MODE.DRAFT_EDIT}/`) + data._id,
                            )
                        }
                    })
                    break

                case MODE.DRAFT_EDIT:
                    await updateDraftOffer(payload, id, (data) => {
                        allowNext = !!data
                    })
                    break

                case MODE.EDIT:
                    allowNext = false
                    if (changedFields.current.size) {
                        if (!isMerchantUser && parent !== OfferPageParent.PAYMENT) {
                            payload = addRemoveMerchants(payload, initialValues.merchants)
                        }
                        await updateOffer({
                            id,
                            offerCode: initialValues.coupon_code || '',
                            payload,
                            success: () => {
                                form.setFields([...changedFields.current].map((name) => ({ name, touched: false })))
                                changedFields.current.clear()
                                setupInitialValues()
                            },
                        })
                    } else {
                        message.info('No changes')
                    }
                    break

                default:
                    break
            }
            if (allowNext) await formRef?.current?.next()
            setSaving(false)
        } catch (error) {
            setSaving(false)
            console.log('Validation failed at handle save and next:', error)
            moveToFirstError(error?.errorFields?.[0]?.name?.[0])
        }
    }

    const getTitle = (mode: MODE) => {
        switch (mode) {
            case MODE.CREATE:
                return 'Create Offer'
            case MODE.DRAFT_EDIT:
                return 'Edit Draft Offer'
            case MODE.EDIT:
                return 'Edit Offer'
        }
    }

    const checkAndPromptLocalState = async () => {
        try {
            await iDb.current.open()
            localDraft.current = await iDb.current.get(getLocalStoreId())

            if (localDraft.current) {
                setOpenLocalDraft(true)
                return true
            }
        } catch (error) {
            console.log('error on fetching local draft:', error)
        }
        return false
    }

    const setupInitialValues = async () => {
        switch (mode as MODE) {
            case MODE.CREATE:
                if (await checkAndPromptLocalState()) return
                setInitialValues({
                    offer_category: 'payment_partner',
                    discount_type: 'fixed',
                    min_cart: 0,
                    show_valid_banks: 'all',
                    view_in_listing: true,
                    limit_discount_amount: false,
                    auto_apply: false,
                })
                break

            case MODE.DRAFT_EDIT:
                getDraftOffer(id, async (data) => {
                    if (!data) return returnToListing(true)
                    setCreatedBy(data.created_by)
                    setInitialValues(await reverseTransformOffer(data, mode))
                })
                break

            case MODE.EDIT:
                getOfferDetails(id, async (data) => {
                    if (!data) return returnToListing(true)
                    setCreatedBy(data.created_by)
                    setInitialValues(await reverseTransformOffer(data, mode))
                })
                break

            default:
                break
        }
    }

    useEffect(() => {
        if (!userData || !Object.keys(userData).length) return

        updateBreadcrumbs((prev) => [
            prev[0],
            ...(breadcrumbs ?? [
                {
                    key: 'settings',
                    href: '/payment/settings',
                    text: 'Payment Settings',
                },
                {
                    key: 'offers',
                    href: '/payment/settings/offers',
                    text: 'Payment Offers',
                },
            ]),
            {
                key: id || 'create',
                href: window.location.href,
                text: id || 'Create',
            },
        ])
        if (!initialValues) setupInitialValues()
    }, [userData, id])

    useEffect(() => {
        if (Object.keys(initialValues || {}).length) {
            summaryRef.current.updateChildState(form.getFieldsValue(true))
            setPrevOfferCategory(initialValues.offer_category)
            if (mode === MODE.EDIT && step > 0) {
                setDropDownData(initialValues.offer_category)
            }
            if (searchParams.get('scrollToMerchants') === 'true') {
                setTimeout(() => {
                    const el = document.querySelector('.ant-spin-container')
                    el?.scrollIntoView({ behavior: 'smooth' })
                    setSearchParams((prev) => {
                        prev.delete('scrollToMerchants')
                        return prev
                    })
                }, 100)
            }
        }
    }, [initialValues])

    const handleFieldsChanges = (fields) => {
        fields
            ?.filter((field) => !!field.touched)
            ?.forEach((field) => {
                changedFields.current.add(field.name?.[0])
            })
    }

    const handleValueChanges = async () => {
        summaryRef.current?.updateChildState(form.getFieldsValue(true))
        if (mode === MODE.CREATE) {
            await iDb.current.update({
                id: getLocalStoreId(),
                data: transformValuesToPayload(
                    form.getFieldsValue(true),
                    parent === OfferPageParent.PAYMENT ? merchantData?.m_id : undefined,
                    mode,
                ),
            })
        }
    }

    const offerCategoryWatch = Form.useWatch('offer_category', form)

    const setDropDownData = useCallback(
        (category?: OFFER_CATEGORY) => {
            getOfferDropDownData(category ?? (offerCategoryWatch as OFFER_CATEGORY), (data) => {
                setMethodOptions(data.payment_method_type)
                setBanks(data.banks)
                setCards(data.cards)
                setEmis(data.emi)
                setPspApps(data.upi)
                setWallets(data.wallet)
            })
        },
        [offerCategoryWatch],
    )

    useEffect(() => {
        if (offerCategoryWatch) {
            if (prevOfferCategory !== null && offerCategoryWatch !== prevOfferCategory) {
                setPrevOfferCategory(offerCategoryWatch)
                form.setFields([
                    { name: 'payment_method_type', value: undefined },
                    { name: 'cards', value: undefined },
                    { name: 'emis', value: undefined },
                    { name: 'wallets', value: undefined },
                    { name: 'psp_apps', value: undefined },
                    { name: 'show_valid_banks', value: 'all' },
                    { name: 'valid_banks', value: undefined },
                    { name: 'auto_apply', value: false },
                ])
            }

            setDropDownData()
        }
    }, [offerCategoryWatch])

    useEffect(() => {
        if (offerCategoryWatch) {
            resetDropDownData()
            setDropDownData()
        }
    }, [merchantData])

    let formSchema: FormSchema = useMemo(() => {
        const category = form?.getFieldValue('offer_category')
        let disableEndDate = false
        if (mode === MODE.EDIT && initialValues) {
            disableEndDate = dayjs().isAfter(initialValues?.period?.[1])
        }
        return {
            stepsProps: { progressDot: true, type: 'navigation' },
            steps: [
                {
                    title: 'Basic Details',
                    sections: [
                        {
                            title: 'Payment Offer Type',
                            fields: [
                                {
                                    name: 'offer_category',
                                    label: '',
                                    type: 'radio',
                                    options: [
                                        {
                                            label: (
                                                <div className='flex gap-x-2'>
                                                    <Icon component={BoltIcon}></Icon>
                                                    Instant Off
                                                </div>
                                            ),
                                            value: OFFER_CATEGORY.PAYMENT_PARTNER,
                                        },
                                        {
                                            label: (
                                                <div className='flex gap-x-2'>
                                                    <Icon component={CashbackIcon}></Icon>
                                                    Cashback
                                                </div>
                                            ),
                                            value: OFFER_CATEGORY.CASHBACK,
                                        },
                                        {
                                            label: (
                                                <div className='flex gap-x-2'>
                                                    <Icon component={NoCostEMIIcon}></Icon>
                                                    No Cost EMI
                                                </div>
                                            ),
                                            value: OFFER_CATEGORY.NO_COST_EMI,
                                        },
                                    ],
                                    rules: [{ required: true, message: 'Please select a type' }],
                                    inputProps: {
                                        optionType: 'button',
                                        buttonStyle: 'solid',
                                        disabled: mode !== MODE.CREATE,
                                        size: 'large',
                                        rootClassName:
                                            'flex gap-x-4 [&_label.ant-radio-button-wrapper]:rounded-lg [&_label.ant-radio-button-wrapper]:border [&_label.ant-radio-button-wrapper]:before:bg-transparent [&_label.ant-radio-button-wrapper.ant-radio-button-wrapper-checked.ant-radio-button-wrapper-disabled]:bg-[#1777FFcc] [&_label.ant-radio-button-wrapper.ant-radio-button-wrapper-checked.ant-radio-button-wrapper-disabled]:text-[#fff]  [&_label.ant-radio-button-wrapper.ant-radio-button-wrapper-checked.ant-radio-button-wrapper-disabled]:border-0',
                                    },
                                },
                            ],
                        },
                        {
                            title: 'Basic Details',
                            fields: [
                                {
                                    name: 'coupon_code',
                                    label: 'Discount Code',
                                    placeholder: 'e.g. GET100OFF',
                                    type: 'input',
                                    tooltip: { title: 'Capitalized by default' },
                                    layout: { span: 12 },
                                    rules: [
                                        { required: true, message: 'Required' },
                                        {
                                            pattern: new RegExp(/^[a-zA-Z0-9\_]*$/),
                                            message: 'Only Alphanumeric Characters and Underscores are allowed',
                                        },
                                    ],
                                    inputProps: {
                                        classNames: { input: 'uppercase' },
                                        disabled: mode === MODE.EDIT,
                                    },
                                },
                                {
                                    name: 'title',
                                    label: 'Offer Name',
                                    placeholder: 'e.g. X Bank Offer',
                                    type: 'input',
                                    layout: { span: 12 },
                                    rules: [{ required: true, message: 'Required' }],
                                },
                                {
                                    name: 'payment_offer_title',
                                    label: 'Offer Title',
                                    placeholder: 'e.g. Get Rs. 100 Off',
                                    type: 'input',
                                    layout: { span: 12 },
                                    rules: [],
                                },
                                {
                                    name: 'payment_offer_sub_title',
                                    label: 'Offer Subtitle',
                                    placeholder: 'Brief detail about the offer',
                                    type: 'input',
                                    layout: { span: 12 },
                                    rules: [],
                                },
                                {
                                    name: 'description',
                                    label: 'Offer Description',
                                    type: 'input',
                                    subType: 'textarea',
                                    placeholder: 'Enter a short description for this offer',
                                    layout: { span: 24 },
                                    rules: [{ required: true, message: 'Required' }],
                                },
                                {
                                    name: 'tnc',
                                    label: 'Terms and Conditions',
                                    type: 'input',
                                    subType: 'bulletTextArea',
                                    placeholder: 'Enter the Terms and Conditions for this offer',
                                    layout: { span: 24 },
                                    rules: [
                                        { required: true, message: 'Required' },
                                        {
                                            validator(_, value) {
                                                if (value && value.includes('|')) {
                                                    return Promise.reject(new Error('Pipe symbols are not allowed'))
                                                }
                                                return Promise.resolve()
                                            },
                                        },
                                    ],
                                },
                                {
                                    name: 'override_badge_text',
                                    label: 'Override Badge Text',
                                    type: 'checkbox',
                                    layout: { span: 12 },
                                    rules: [],
                                    condition: { field: 'offer_category', value: 'cashback_offer' },
                                    inputProps: {
                                        disabled: mode === MODE.EDIT,
                                        className: 'mb-10',
                                    },
                                },
                                {
                                    name: 'offer_title',
                                    label: 'Badge Text',
                                    tooltip: { title: 'Existing badge text will be replaced with below text' },
                                    placeholder: 'e.g. Get 10% Cashback!',
                                    type: 'input',
                                    layout: { span: 12 },
                                    rules: [{ required: mode !== MODE.EDIT, message: 'Required' }],
                                    condition: { field: 'override_badge_text', value: true },
                                    inputProps: { disabled: mode === MODE.EDIT },
                                },
                                {
                                    name: 'logo_url',
                                    label: 'Logo URL',
                                    type: 'input',
                                    placeholder: 'https://your-domain/logo-path',
                                    layout: { span: 24 },
                                    rules: [
                                        {
                                            validator: async (_, value) => {
                                                if (!value) return Promise.resolve()
                                                if (value && value.startsWith('https://')) {
                                                    return new Promise((res, rej) => {
                                                        try {
                                                            const imgTag = document.createElement('img')
                                                            imgTag.onload = () => res(true)
                                                            imgTag.onerror = () => rej('Invalid image URL')
                                                            imgTag.src = value
                                                        } catch (_) {}
                                                    })
                                                }
                                                return Promise.reject(new Error('Must be a valid URL'))
                                            },
                                        },
                                    ],
                                },
                                {
                                    name: 'view_in_listing',
                                    label: 'View in Listing',
                                    type: 'radio',
                                    layout: { span: 12 },
                                    options: [
                                        { label: 'Yes', value: true },
                                        { label: 'No', value: false },
                                    ],
                                    rules: [{ required: true, message: 'Required' }],
                                    condition: {
                                        field: 'offer_category',
                                        operator: 'not',
                                        value: OFFER_CATEGORY.NO_COST_EMI,
                                    },
                                },
                            ],
                        },
                        {
                            fields: [
                                {
                                    name: 'period',
                                    label: 'Active Period',
                                    type: 'dateRange',
                                    rules: [
                                        { type: 'array' as const, required: true, message: 'Please select time!' },
                                        {
                                            validator(_, value) {
                                                if (value?.length && value[1] && dayjs().isAfter(value[1])) {
                                                    return Promise.reject(
                                                        new Error('End date & time must be greater than current time'),
                                                    )
                                                }
                                                return Promise.resolve()
                                            },
                                        },
                                    ],
                                    inputProps: {
                                        showTime: true,
                                        onOk: (...vals) => {
                                            if (mode !== MODE.EDIT) return
                                            form.setFieldValue('period', vals?.[0])
                                            form.validateFields(['period'])
                                            changedFields.current.add('period')
                                        },
                                        minDate: dayjs(),
                                        maxDate: dayjs().add(100, 'y'),
                                        presets: [],
                                        allowClear: false,
                                        disabled: [mode === MODE.EDIT, disableEndDate],
                                    },
                                },
                            ],
                        },
                    ],
                },
                {
                    title: 'Conditions',
                    sections: [
                        {
                            fields: [
                                {
                                    name: 'discount_type',
                                    label: <>{category === OFFER_CATEGORY.CASHBACK ? 'Cashback' : 'Discount'} Type</>,
                                    type: 'radio',
                                    options: [
                                        {
                                            label: 'Fixed',
                                            value: 'fixed',
                                        },
                                        {
                                            label: 'Percentage',
                                            value: 'percentage',
                                        },
                                    ],
                                    layout: { span: 24 },
                                    rules: [{ required: true, message: 'Please select a discount type' }],
                                    inputProps: {
                                        disabled: mode === MODE.EDIT,
                                        onChange: () => form.validateFields(['min_cart']),
                                    },
                                    condition: {
                                        field: 'offer_category',
                                        operator: 'not',
                                        value: OFFER_CATEGORY.NO_COST_EMI,
                                    },
                                },
                                {
                                    name: 'amount',
                                    label: <>{category === OFFER_CATEGORY.CASHBACK ? 'Cashback' : 'Discount'} Amount</>,
                                    placeholder: 'e.g. 100',
                                    type: 'input',
                                    subType: 'number',
                                    layout: { span: 12 },
                                    rules: [
                                        { required: true, message: 'Please enter a discount amount' },
                                        { min: 0, type: 'number' },
                                    ],
                                    inputProps: {
                                        min: 0,
                                        addonBefore: currencySymbol,
                                        onChange: () => form.validateFields(['min_cart']),
                                        disabled: mode === MODE.EDIT,
                                    },
                                    condition: {
                                        conditions: [
                                            {
                                                field: 'offer_category',
                                                operator: 'not',
                                                value: OFFER_CATEGORY.NO_COST_EMI,
                                            },
                                            { field: 'discount_type', value: 'fixed' },
                                        ],
                                        operation: 'every',
                                    },
                                },
                                {
                                    name: 'percentage',
                                    label: (
                                        <>{category === OFFER_CATEGORY.CASHBACK ? 'Cashback' : 'Discount'} Percentage</>
                                    ),
                                    placeholder: 'e.g. 10',
                                    type: 'input',
                                    subType: 'number',
                                    layout: { span: 8 },
                                    rules: [
                                        { required: true, message: 'Please enter a discount percentage' },
                                        { min: 0, max: 100, type: 'number' },
                                    ],
                                    inputProps: { min: 0, max: 100, addonAfter: '%', disabled: mode === MODE.EDIT },
                                    condition: {
                                        conditions: [
                                            {
                                                field: 'offer_category',
                                                operator: 'not',
                                                value: OFFER_CATEGORY.NO_COST_EMI,
                                            },
                                            { field: 'discount_type', value: 'percentage' },
                                        ],
                                        operation: 'every',
                                    },
                                },
                                {
                                    name: 'max_discount_amount',
                                    label: (
                                        <>
                                            Maximum {category === OFFER_CATEGORY.CASHBACK ? 'Cashback' : 'Discount'}{' '}
                                            Amount
                                        </>
                                    ),
                                    placeholder: 'e.g. 500',
                                    type: 'input',
                                    subType: 'number',
                                    layout: { span: 12 },
                                    rules: [
                                        { required: true, message: 'Please enter a maximum discount amount' },
                                        { min: 0, type: 'number' },
                                    ],
                                    inputProps: {
                                        min: 0,
                                        addonBefore: currencySymbol,
                                        onChange: () => form.validateFields(['min_cart']),
                                        disabled: mode === MODE.EDIT,
                                    },
                                    condition: {
                                        conditions: [
                                            {
                                                field: 'offer_category',
                                                operator: 'not',
                                                value: OFFER_CATEGORY.NO_COST_EMI,
                                            },
                                            { field: 'discount_type', value: 'percentage' },
                                            { field: 'limit_discount_amount', value: true },
                                        ],
                                        operation: 'every',
                                    },
                                },
                                {
                                    name: 'limit_discount_amount',
                                    label: (
                                        <>
                                            Set {category === OFFER_CATEGORY.CASHBACK ? 'Cashback' : 'Discount'} Capping
                                        </>
                                    ),
                                    type: 'checkbox',
                                    layout: { span: 24 },
                                    rules: [],
                                    inputProps: {
                                        onChange: () => form.validateFields(['min_cart']),
                                        disabled: mode === MODE.EDIT,
                                    },
                                    condition: {
                                        conditions: [
                                            {
                                                field: 'offer_category',
                                                operator: 'not',
                                                value: OFFER_CATEGORY.NO_COST_EMI,
                                            },
                                            { field: 'discount_type', value: 'percentage' },
                                        ],
                                        operation: 'every',
                                    },
                                },
                            ],
                        },
                        {
                            title: 'Max Offer Usage',
                            fields: [
                                {
                                    name: 'max_coupon_count',
                                    label: 'Limit number of times this offer can be used in total',
                                    placeholder: 'e.g. 1000',
                                    type: 'input',
                                    subType: 'number',
                                    layout: { span: 24 },
                                    rules: [
                                        { required: true, message: 'Please enter maximum usage count' },
                                        { min: 1, type: 'number' },
                                    ],
                                    inputProps: { min: 1, step: 1, disabled: mode === MODE.EDIT },
                                },
                                {
                                    name: 'coupon_per_customer',
                                    label: 'Limit number of times this offer can be used per customer',
                                    placeholder: 'e.g. 1000',
                                    type: 'input',
                                    subType: 'number',
                                    layout: { span: 24 },
                                    rules: [{ min: 1, type: 'number' }],
                                    inputProps: { min: 1, step: 1, disabled: mode === MODE.EDIT },
                                },
                            ],
                        },
                        {
                            title: 'Order Amount',
                            description: 'Enter the minimum and maximum amount for which the offer will be applied',
                            fields: [
                                {
                                    name: 'min_cart',
                                    label: 'Minimum Cart Value',
                                    placeholder: 'e.g. 100',
                                    type: 'input',
                                    subType: 'number',
                                    layout: { span: 8 },
                                    rules: [
                                        { required: true, message: 'Please enter minimum cart value' },
                                        { min: 0, type: 'number' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (
                                                    typeof value !== 'number' ||
                                                    isNaN(getFieldValue('amount')) ||
                                                    getFieldValue('discount_type') === 'percentage' ||
                                                    value > getFieldValue('amount')
                                                ) {
                                                    return Promise.resolve()
                                                }
                                                return Promise.reject(
                                                    new Error('Minimum cart value must be greater than offer amount.'),
                                                )
                                            },
                                        }),
                                    ],
                                    inputProps: { min: 0, addonBefore: currencySymbol, disabled: mode === MODE.EDIT },
                                },
                                {
                                    name: 'max_cart',
                                    label: 'Maximum Cart Value',
                                    placeholder: 'e.g. 10,000',
                                    type: 'input',
                                    subType: 'number',
                                    layout: { span: 16 },
                                    rules: [
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (typeof value !== 'number' || value > getFieldValue('min_cart')) {
                                                    return Promise.resolve()
                                                }
                                                return Promise.reject(
                                                    new Error(
                                                        'Maximum cart value must be greater than minimum cart value',
                                                    ),
                                                )
                                            },
                                        }),
                                    ],
                                    inputProps: { min: 0, addonBefore: currencySymbol, disabled: mode === MODE.EDIT },
                                },
                            ],
                        },
                    ],
                },
                {
                    title: 'Applicable On',
                    sections: [
                        {
                            fields: [
                                {
                                    name: 'payment_method_type',
                                    label: 'Applicable On',
                                    placeholder: 'Select Payment Method',
                                    type: 'select',
                                    layout: { span: 8 },
                                    options: methodOptions,
                                    rules: [{ required: true, message: 'Payment Method is required' }],
                                    inputProps: { disabled: mode === MODE.EDIT },
                                    condition: {
                                        field: 'offer_category',
                                        operator: 'not',
                                        value: OFFER_CATEGORY.NO_COST_EMI,
                                    },
                                },
                                {
                                    name: 'cards',
                                    label: 'Card Methods',
                                    placeholder: 'Select Card Methods',
                                    type: 'select',
                                    layout: { span: 16 },
                                    options: cards,
                                    rules: [{ required: true, message: 'Card Method is required' }],
                                    condition: { field: 'payment_method_type', value: 'cards' },
                                    inputProps: { mode: 'multiple', disabled: mode === MODE.EDIT },
                                },
                                {
                                    name: 'emis',
                                    label: 'EMI Methods',
                                    placeholder: 'Select EMI Methods',
                                    type: 'select',
                                    layout: { span: 16 },
                                    options: emis,
                                    rules: [{ required: true, message: 'EMI Method is required' }],
                                    condition: { field: 'payment_method_type', value: 'emi' },
                                    inputProps: { mode: 'multiple', disabled: mode === MODE.EDIT },
                                },
                            ],
                        },
                        {
                            title: 'Card Details',
                            fields: [
                                {
                                    name: 'show_valid_banks',
                                    label: 'Select Banks',
                                    placeholder: 'Select Banks',
                                    type: 'radio',
                                    layout: { span: 9 },
                                    options: [
                                        { label: 'All', value: 'all' },
                                        { label: 'Specific Bank(s)', value: 'specific' },
                                    ],
                                    rules: [],
                                    condition: {
                                        conditions: [
                                            {
                                                field: 'payment_method_type',
                                                value: 'emi',
                                            },
                                            {
                                                field: 'offer_category',
                                                operator: 'not',
                                                value: OFFER_CATEGORY.NO_COST_EMI,
                                            },
                                        ],
                                        operation: 'every',
                                    },
                                    inputProps: { disabled: mode === MODE.EDIT },
                                },
                                {
                                    name: 'valid_banks',
                                    label: 'Banks',
                                    placeholder: 'Select Specific Banks',
                                    type: 'select',
                                    layout: { span: 15 },
                                    options: banks,
                                    rules: [{ required: true, message: 'Banks are required' }],
                                    condition: { field: 'show_valid_banks', value: 'specific' },
                                    inputProps: {
                                        mode: 'multiple',
                                        allowClear: true,
                                        maxTagCount: mode === MODE.EDIT ? undefined : 1,
                                        disabled: mode === MODE.EDIT,
                                    },
                                },
                                {
                                    name: 'bins',
                                    label: 'Add BINs',
                                    placeholder: 'minimum 6 digits',
                                    type: 'select',
                                    tooltip: { title: 'Paste in comma-separated or column data' },
                                    layout: { span: 24 },
                                    options: [],
                                    rules: [{ required: true, message: 'BINs are required' }],
                                    condition: {
                                        field: 'payment_method_type',
                                        operator: 'in',
                                        value: ['emi', 'cards'],
                                    },
                                    inputProps: {
                                        allowClear: true,
                                        mode: 'tags',
                                        tokenSeparators: [', ', ',', ' ', '\n', '\r\n'],
                                        maxCount: 500,
                                        disabled: mode === MODE.EDIT,
                                        open: false,
                                        suffixIcon: null,
                                        onChange: (values) => {
                                            form.setFieldValue(
                                                'bins',
                                                values?.filter((val) => !isNaN(val) && val.length >= 6),
                                            )
                                            handleValueChanges()
                                        },
                                    },
                                },
                                {
                                    name: '',
                                    type: 'custom_direct',
                                    render: (props) => <BankCodesInput {...props} />,
                                    inputProps: {
                                        banks,
                                        disabled: mode === MODE.EDIT,
                                    },
                                    condition: {
                                        field: 'offer_category',
                                        value: OFFER_CATEGORY.NO_COST_EMI,
                                    },
                                },
                            ],
                        },
                        {
                            title: 'Select Wallet',
                            fields: [
                                {
                                    name: 'wallets',
                                    label: 'Select Wallet',
                                    placeholder: 'Select Wallet',
                                    type: 'select',
                                    layout: { span: 8 },
                                    options: wallets,
                                    rules: [{ required: true, message: 'Wallet is required' }],
                                    condition: { field: 'payment_method_type', value: 'wallet' },
                                    inputProps: {
                                        disabled: mode === MODE.EDIT,
                                    },
                                },
                            ],
                        },
                        {
                            title: 'Select UPI App',
                            fields: [
                                {
                                    name: 'psp_apps',
                                    label: 'Select UPI App',
                                    placeholder: 'Select UPI App',
                                    type: 'select',
                                    layout: { span: 8 },
                                    options: pspApps,
                                    rules: [{ required: true, message: 'UPI App is required' }],
                                    condition: { field: 'payment_method_type', value: 'upi' },
                                    inputProps: {
                                        disabled: mode === MODE.EDIT,
                                    },
                                },
                            ],
                        },
                        {
                            fields: [
                                {
                                    name: 'auto_apply',
                                    label: 'Method of Application',
                                    type: 'radio',
                                    layout: { span: 24 },
                                    options: [
                                        {
                                            label: 'Coupon Based',
                                            value: false,
                                        },
                                        {
                                            label: 'Auto Apply',
                                            value: true,
                                        },
                                    ],
                                    rules: [{ required: true, message: 'Required' }],
                                    condition: { field: 'offer_category', value: OFFER_CATEGORY.PAYMENT_PARTNER },
                                    inputProps: { disabled: mode === MODE.EDIT },
                                },
                            ],
                        },
                        !isMerchantUser && parent !== OfferPageParent.PAYMENT
                            ? {
                                  title: 'Select Merchants',
                                  description: 'This option allow you to add & remove merchants for this offer.',
                                  fields: [
                                      {
                                          name: 'is_global_offer',
                                          label: 'Global Offer',
                                          type: 'radio',
                                          options: [
                                              { label: 'Yes', value: true },
                                              { label: 'No', value: false },
                                          ],
                                          rules: [{ required: true, message: 'Please select an option' }],
                                          inputProps: {
                                              disabled: mode === MODE.EDIT,
                                          },
                                      },
                                      {
                                          name: 'merchants',
                                          label: '',
                                          type: 'custom',
                                          render: (props) => <MerchantSelector {...props} />,
                                          rules: [],
                                          inputProps: {
                                              isEdit: mode === MODE.EDIT,
                                              id,
                                          },
                                      },
                                  ],
                              }
                            : null,
                    ].filter((val) => !!val) as Section[],
                },
            ],
        }
    }, [offerCategoryWatch, mode, isMerchantUser, methodOptions, cards, emis, banks, wallets, pspApps])

    const restoreLocalDraft = async () => {
        setInitialValues(await reverseTransformOffer(localDraft.current.data, mode))
        setOpenLocalDraft(false)
    }

    const startFresh = async () => {
        localDraft.current = null
        await deleteDraft()
        setOpenLocalDraft(false)
        setupInitialValues()
    }

    return !!mode ? (
        <>
            <div className='w-full flex gap-x-4'>
                <div className='w-full flex flex-col gap-y-3'>
                    <div className='flex justify-between items-start'>
                        <p className='text-base capitalize font-medium mb-1'>{getTitle(mode)}</p>
                        <div className='flex items-center gap-x-2'>
                            {mode === MODE.EDIT ? <Button onClick={() => returnToListing(true)}>Back</Button> : null}
                            {[MODE.CREATE, MODE.DRAFT_EDIT].includes(mode) ? (
                                <Button onClick={handleSaveAsDraft} loading={savingDraft} disabled={saving}>
                                    Save as Draft
                                </Button>
                            ) : null}
                            <Button
                                variant='primary'
                                onClick={handleSaveAndNext}
                                loading={saving}
                                disabled={savingDraft}
                            >
                                {[MODE.CREATE, MODE.DRAFT_EDIT].includes(mode)
                                    ? step < 2
                                        ? 'Save & Next'
                                        : 'Publish'
                                    : 'Update'}
                            </Button>
                        </div>
                    </div>
                    <div className='bg-white rounded-lg border border-gray-400 p-3 mb-4'>
                        {initialValues ? (
                            <JsonDrivenFormWizard
                                ref={formRef}
                                hideNavigationButtons
                                formSchema={formSchema}
                                onFinish={handleFinish}
                                step={step}
                                onStepChange={setStep}
                                form={form}
                                onFormFieldsChange={handleFieldsChanges}
                                onFormValuesChange={handleValueChanges}
                                initialValues={initialValues}
                                moveToLatest={mode !== MODE.EDIT}
                                freeForm={mode === MODE.EDIT}
                            />
                        ) : (
                            <div className='w-full flex items-center justify-center text-lg gap-x-2 p-3'>
                                Loading
                                <LoadingOutlined spin style={{ color: 'blue', fontSize: '1.5rem' }} />
                            </div>
                        )}
                    </div>
                </div>
                <div className='min-w-96 bg-white rounded-lg p-3 mb-4 overflow-y-auto'>
                    <Summary ref={summaryRef} currencySymbol={currencySymbol} createdBy={createdBy} />
                </div>
            </div>
            <Modal
                open={openLocalDraft}
                title='Restore Unsaved Changes?'
                onOk={restoreLocalDraft}
                onCancel={startFresh}
                cancelText='Start fresh'
                okText='Restore draft'
                centered
            >
                It looks like you have unsaved changes from your last session. Would you like to restore your draft or
                start fresh?
            </Modal>
        </>
    ) : null
}
